import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { GripVertical, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import type { PipelineView, StageCountView } from '../../lib/client.ts';
import { cn } from '../../lib/utils.ts';
import { StageChip } from '../pipelines/stage-chip.tsx';
import { Button } from '../ui/button.tsx';
import type { BoardDensity } from './layout-toggle.tsx';

type Stage = PipelineView['stages'][number];
type Page<T> = { items: T[]; nextCursor: string | null };
const COLUMN_PAGE = 20;

/** One column of a board: `id` is what a card's `stageOf` returns and what `move` is given. */
export interface BoardColumn {
  id: string;
  name: string;
  /** What heads the column: a pipeline stage's chip, a task status's badge. */
  header: ReactNode;
}
/** A pipeline stage as a column: the stage itself, so `moved` can read its colour and kind. */
export type StageColumn = Stage & BoardColumn;

/**
 * Where a board's cards come from and how one moves (RELAY-1038). The server does the
 * grouping: `counts` gives every column its total, `page` one column's cards a page at a time,
 * so no board ever holds a whole data set. `move` is the same endpoint the table and the record
 * page use, so a drag writes the same audit, history and activity rows as any other move.
 * Without `counts` a column's total is what it has loaded once it has loaded everything.
 */
export interface BoardSource<T, C extends BoardColumn = StageColumn> {
  /** What the board's data depends on (filter, search, parent): part of every query key. */
  scope: readonly unknown[];
  counts?: () => Promise<StageCountView[]>;
  page: (stageId: string, cursor: string | null, limit: number) => Promise<Page<T>>;
  move: (item: T, stageId: string, extra?: unknown) => Promise<unknown>;
  /**
   * Before a drop into `column` (optional): ask for what the move needs, such as a hire's kind on
   * a shortlist (TS-925). Null cancels the drop, nothing moved and no error; otherwise `extra`
   * goes to `move`.
   */
  confirm?: (item: T, column: C) => Promise<{ extra?: unknown } | null>;
  idOf: (item: T) => string;
  stageOf: (item: T) => string;
  titleOf: (item: T) => string;
  /** The card as it will read once moved, shown at once (the optimistic update). */
  moved: (item: T, column: C) => T;
  /**
   * A card that cannot move (optional), such as a candidate taken off a shortlist: no drag
   * handle, its title greyed out, `data-inactive` on the card. What it renders stays clickable.
   */
  inactiveOf?: (item: T) => boolean;
  /**
   * Whether this one card may move, on a board where moving is allowed at all (optional): no
   * drag handle, but not greyed out, such as a task the caller may read but not change.
   */
  canMoveItem?: (item: T) => boolean;
}

type BoardProps<T, C extends BoardColumn> = {
  /** The board's name in query keys and test ids: `projects`, `deals`, `jobs`, `shortlist`. */
  name: string;
  source: BoardSource<T, C>;
  /** `compact` makes every column half as wide (w-36 against w-72). */
  density?: BoardDensity;
  canMove: boolean;
  renderCard: (item: T) => ReactNode;
  onOpen?: ((item: T) => void) | undefined;
  /** After a move settles: anything else on the page that shows these records re-reads. */
  onMoved?: () => void | Promise<void>;
};

/**
 * A kanban board over one pipeline: a column per stage, lazily loaded, and drag-and-drop
 * (dnd-kit) that moves the card at once and rolls back if the server refuses. Every card has a
 * drag handle; with the keyboard, Space picks a card up, ←/→ jump between columns and Space
 * drops it.
 */
export function KanbanBoard<T>({
  pipeline,
  hideTerminal = false,
  ...props
}: BoardProps<T, StageColumn> & { pipeline: PipelineView; hideTerminal?: boolean }) {
  const columns = useMemo(
    () =>
      pipeline.stages
        .filter((s) => !hideTerminal || !s.isTerminal)
        .map((stage) => ({
          ...stage,
          header: (
            <StageChip
              family={pipeline.family}
              colour={stage.colour}
              name={stage.name}
              terminalKind={stage.terminalKind}
            />
          ),
        })),
    [pipeline, hideTerminal],
  );
  return <Board {...props} columns={columns} />;
}

/**
 * The same board over any fixed set of columns (TS-1742: a task's statuses), with a column per
 * entry of `columns`. A pipeline board is `KanbanBoard`.
 */
export function Board<T, C extends BoardColumn>({
  name,
  columns,
  source,
  density = 'normal',
  canMove,
  renderCard,
  onOpen,
  onMoved,
}: BoardProps<T, C> & { columns: readonly C[] }) {
  const qc = useQueryClient();
  const root = ['board', name, ...source.scope] as const;
  const countsKey = [...root, 'counts'];
  const columnKey = (stageId: string) => [...root, 'column', stageId];
  const counts = useQuery({
    queryKey: countsKey,
    queryFn: () => source.counts!(),
    enabled: !!source.counts,
  });
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: (v: { item: T; from: string; to: string; extra?: unknown }) =>
      source.move(v.item, v.to, v.extra),
    onMutate: async ({ item, from, to }) => {
      await qc.cancelQueries({ queryKey: root });
      const keys = [columnKey(from), columnKey(to), countsKey];
      const snapshot = keys.map((k) => [k, qc.getQueryData(k)] as const);
      const id = source.idOf(item);
      const column = columns.find((c) => c.id === to)!;
      qc.setQueryData<InfiniteData<Page<T>>>(columnKey(from), (d) =>
        d
          ? {
              ...d,
              pages: d.pages.map((p) => ({
                ...p,
                items: p.items.filter((x) => source.idOf(x) !== id),
              })),
            }
          : d,
      );
      qc.setQueryData<InfiniteData<Page<T>>>(columnKey(to), (d) =>
        d
          ? {
              ...d,
              pages: d.pages.map((p, i) =>
                i === 0 ? { ...p, items: [source.moved(item, column), ...p.items] } : p,
              ),
            }
          : d,
      );
      qc.setQueryData<StageCountView[]>(countsKey, (c) => (c ? bump(c, from, to) : c));
      return snapshot;
    },
    onError: (err, _v, snapshot) => {
      for (const [k, data] of snapshot ?? []) qc.setQueryData(k, data);
      setError(describeError(err));
    },
    onSuccess: () => setError(null),
    onSettled: async () => {
      // The server's word on both columns and the totals (a rule may have moved more).
      await qc.invalidateQueries({ queryKey: root });
      await onMoved?.();
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: jumpBetweenColumns }),
  );
  // The card being dragged, drawn in an overlay so the board's scroll box never clips it.
  const [dragging, setDragging] = useState<T | null>(null);
  const onDragStart = (e: DragStartEvent) =>
    setDragging((e.active.data.current as { item: T } | undefined)?.item ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const data = e.active.data.current as { item: T; from: string } | undefined;
    const to = e.over ? String(e.over.id) : null;
    if (!data || !to || to === data.from) return;
    const column = columns.find((c) => c.id === to);
    if (!source.confirm || !column) {
      move.mutate({ item: data.item, from: data.from, to });
      return;
    }
    void source.confirm(data.item, column).then((answer) => {
      if (answer) move.mutate({ item: data.item, from: data.from, to, extra: answer.extra });
    });
  };
  const stageName = (id: unknown) => columns.find((c) => c.id === id)?.name ?? 'nowhere';
  const titleOf = (data: unknown) => (data as { title?: string } | undefined)?.title ?? 'the card';

  return (
    <div className="group/board flex flex-col gap-2" data-density={density}>
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={columnCollision}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragging(null)}
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => `Picked up ${titleOf(active.data.current)}.`,
            onDragOver: ({ active, over }) =>
              `${titleOf(active.data.current)} is over ${stageName(over?.id)}.`,
            onDragEnd: ({ active, over }) =>
              over
                ? `${titleOf(active.data.current)} dropped in ${stageName(over.id)}.`
                : `${titleOf(active.data.current)} was not moved.`,
            onDragCancel: ({ active }) => `Moving ${titleOf(active.data.current)} was cancelled.`,
          },
          screenReaderInstructions: {
            draggable:
              'Press space to pick the card up, the left and right arrows to choose a column, space to drop it, escape to cancel.',
          },
        }}
      >
        <div
          className={cn('flex overflow-x-auto pb-2', density === 'compact' ? 'gap-2' : 'gap-3')}
          data-testid={`${name}-board`}
        >
          {columns.map((column) => (
            <Column
              key={column.id}
              density={density}
              column={column}
              total={
                counts.data
                  ? (counts.data.find((c) => c.stageId === column.id)?.count ?? 0)
                  : undefined
              }
              queryKey={columnKey(column.id)}
              source={source}
              canMove={canMove && !move.isPending}
              renderCard={renderCard}
              onOpen={onOpen}
            />
          ))}
        </div>
        <DragOverlay>
          {dragging ? (
            <CardShell title={source.titleOf(dragging)} lifted>
              {renderCard(dragging)}
            </CardShell>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function bump(counts: StageCountView[], from: string, to: string): StageCountView[] {
  const next = counts.map((c) =>
    c.stageId === from ? { ...c, count: Math.max(c.count - 1, 0) } : c,
  );
  return next.some((c) => c.stageId === to)
    ? next.map((c) => (c.stageId === to ? { ...c, count: c.count + 1 } : c))
    : [...next, { stageId: to, count: 1 }];
}

/** The pointer decides where it is; with the keyboard (no pointer) the moved card's rectangle does. */
const columnCollision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length ? hits : rectIntersection(args);
};

/** ←/→ jump the picked-up card to the centre of the next column rather than 25 px at a time. */
const jumpBetweenColumns: KeyboardCoordinateGetter = (event, { context, currentCoordinates }) => {
  if (event.code !== 'ArrowRight' && event.code !== 'ArrowLeft') return undefined;
  const card = context.collisionRect;
  if (!card) return undefined;
  event.preventDefault();
  const centre = card.left + card.width / 2;
  const rects = context.droppableContainers
    .getEnabled()
    .map((c) => context.droppableRects.get(c.id))
    .filter((r): r is NonNullable<typeof r> => !!r)
    .sort((a, b) => a.left - b.left);
  const target =
    event.code === 'ArrowRight'
      ? rects.find((r) => r.left > centre)
      : [...rects].reverse().find((r) => r.left + r.width < centre);
  if (!target) return undefined;
  return {
    x: currentCoordinates.x + (target.left + target.width / 2 - centre),
    y: currentCoordinates.y + (target.top + 40 - card.top),
  };
};

/** True once the element has been on screen (and stays true): a column loads when first seen. */
function useSeen<E extends Element>(): [React.RefObject<E | null>, boolean] {
  const ref = useRef<E>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (seen || !el) return;
    // No IntersectionObserver (old browsers, jsdom): load straight away rather than never.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

function Column<T, C extends BoardColumn>({
  density,
  column,
  total: counted,
  queryKey,
  source,
  canMove,
  renderCard,
  onOpen,
}: {
  density: BoardDensity;
  column: C;
  total: number | undefined;
  queryKey: unknown[];
  source: BoardSource<T, C>;
  canMove: boolean;
  renderCard: (item: T) => ReactNode;
  onOpen: ((item: T) => void) | undefined;
}) {
  const [seenRef, seen] = useSeen<HTMLElement>();
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const cards = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => source.page(column.id, pageParam, COLUMN_PAGE),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: seen,
  });
  const items = cards.data?.pages.flatMap((p) => p.items) ?? [];
  // A source with no `counts` (TS-1742): the column knows its total once it has every page.
  const total =
    counted ?? (!source.counts && cards.isSuccess && !cards.hasNextPage ? items.length : undefined);
  const left = total !== undefined ? Math.max(total - items.length, 0) : undefined;
  return (
    <section
      ref={(el) => {
        seenRef.current = el;
        setNodeRef(el);
      }}
      className={cn(
        'bg-muted/70 border-border/70 flex shrink-0 flex-col rounded-2xl border transition-colors',
        density === 'compact' ? 'w-36 gap-1.5 rounded-xl p-1.5' : 'w-72 gap-2 p-2.5',
        isOver && 'border-brand bg-brand-soft/60',
      )}
      data-testid="board-column"
      data-stage-id={column.id}
      data-density={density}
      data-over={isOver || undefined}
      aria-label={`${column.name} column`}
    >
      <header
        className={cn(
          'flex items-center justify-between',
          density === 'compact' ? 'gap-1 px-0.5' : 'gap-2 px-1',
        )}
      >
        {column.header}
        <span
          className="text-muted-foreground shrink-0 text-xs tabular-nums"
          data-testid="column-total"
        >
          {total ?? '…'}
        </span>
      </header>
      {(!seen || cards.isPending) && (
        <p className="text-muted-foreground flex items-center gap-1.5 px-1 text-xs">
          <Loader2 className="size-3.5 animate-spin" /> Loading…
        </p>
      )}
      {cards.error && (
        <p className="text-danger px-1 text-xs" role="alert">
          {describeError(cards.error)}
        </p>
      )}
      {seen && cards.isSuccess && items.length === 0 && (
        <p className="text-muted-foreground px-1 text-xs">Nothing here.</p>
      )}
      <ol className={cn('flex flex-col', density === 'compact' ? 'gap-1.5' : 'gap-2')}>
        {items.map((item) => (
          <li key={source.idOf(item)}>
            <Card
              item={item}
              source={source}
              stageId={column.id}
              canMove={canMove && (source.canMoveItem?.(item) ?? true)}
              onOpen={onOpen}
            >
              {renderCard(item)}
            </Card>
          </li>
        ))}
      </ol>
      {cards.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          disabled={cards.isFetchingNextPage}
          onClick={() => void cards.fetchNextPage()}
        >
          Load more{left ? ` (${left} more)` : ''}
        </Button>
      )}
    </section>
  );
}

function Card<T, C extends BoardColumn>({
  item,
  source,
  stageId,
  canMove,
  onOpen,
  children,
}: {
  item: T;
  source: BoardSource<T, C>;
  stageId: string;
  canMove: boolean;
  onOpen: ((item: T) => void) | undefined;
  children: ReactNode;
}) {
  const title = source.titleOf(item);
  const inactive = source.inactiveOf?.(item) ?? false;
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: source.idOf(item),
    data: { item, from: stageId, title },
    disabled: !canMove || inactive,
  });
  return (
    <CardShell
      ref={setNodeRef}
      title={title}
      dimmed={isDragging}
      inactive={inactive}
      onOpen={onOpen ? () => onOpen(item) : undefined}
      handle={
        canMove && !inactive ? (
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="text-muted-foreground hover:text-foreground -ml-1 cursor-grab self-start rounded p-0.5 active:cursor-grabbing group-data-[density=compact]/board:-ml-0.5 group-data-[density=compact]/board:p-0"
            aria-label={`Move ${title}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4 group-data-[density=compact]/board:size-3.5" />
          </button>
        ) : null
      }
    >
      {children}
    </CardShell>
  );
}

/** A card's look, shared by the card in its column and the copy that follows the pointer. */
function CardShell({
  ref,
  title,
  handle,
  dimmed,
  inactive,
  lifted,
  onOpen,
  children,
}: {
  ref?: React.Ref<HTMLElement>;
  title: string;
  handle?: ReactNode;
  dimmed?: boolean;
  inactive?: boolean;
  lifted?: boolean;
  onOpen?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <article
      ref={ref}
      className={cn(
        'bg-card border-border hover:border-border-hover shadow-card hover:shadow-raised flex gap-1.5 rounded-xl border p-3 text-sm transition-shadow',
        'group-data-[density=compact]/board:gap-1 group-data-[density=compact]/board:rounded-lg group-data-[density=compact]/board:p-1.5 group-data-[density=compact]/board:text-xs',
        dimmed && 'opacity-40',
        inactive && 'bg-muted/50 shadow-none [&_button:not([data-keep-active])]:opacity-50',
        lifted && 'ring-brand/60 cursor-grabbing shadow-lg ring-2',
      )}
      data-testid={lifted ? 'board-card-overlay' : 'board-card'}
      data-inactive={inactive ? 'true' : undefined}
    >
      {handle ?? (lifted ? <GripVertical className="text-muted-foreground size-4" /> : null)}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {onOpen ? (
          <button
            type="button"
            // A long unbreakable name (a website) must not spill past the card: cut like its fields.
            className="max-w-full truncate text-left font-medium hover:underline"
            onClick={onOpen}
            aria-label={`Open ${title}`}
            title={title}
          >
            {title}
          </button>
        ) : (
          <p className="truncate font-medium" title={title}>
            {title}
          </p>
        )}
        {children}
      </div>
    </article>
  );
}
